import { useEffect, useRef, useState } from 'react';
import { useRouteError } from 'react-router-dom';

import { isStalePageFileError, reloadForNewDeploy } from '../services/stalePageFiles';

/**
 * Shown when a page fails to render. After a deploy it reloads once onto the
 * new version; otherwise it offers a refresh instead of the router's
 * developer error screen.
 */
export function AppErrorScreen() {
  const error = useRouteError();
  const isStale = isStalePageFileError(error);
  const [reloading, setReloading] = useState(isStale);
  const attemptedReload = useRef(false);

  useEffect(() => {
    if (!isStale || attemptedReload.current) return;
    attemptedReload.current = true;
    setReloading(reloadForNewDeploy());
  }, [isStale]);

  if (reloading) {
    return (
      <main className="font-outfit min-h-screen flex items-center justify-center px-5">
        <p className="text-sm text-grey2" role="status" data-testid="app-error-reloading">
          Updating to the latest version...
        </p>
      </main>
    );
  }

  return (
    <main
      className="font-outfit min-h-screen flex items-center justify-center px-5"
      data-testid="app-error-screen"
    >
      <section className="max-w-md flex flex-col items-center text-center">
        <h1 className="text-2xl font-semibold">Something went wrong</h1>
        <p className="mt-3 text-sm text-grey2">
          This page didn't load. Refreshing usually fixes it.
        </p>
        <button
          type="button"
          id="app-error-refresh"
          data-testid="app-error-refresh"
          aria-label="Refresh page"
          onClick={() => window.location.reload()}
          className="mt-6 rounded-md bg-primary px-5 py-3 text-sm font-medium text-white hover:bg-black"
        >
          Refresh page
        </button>
      </section>
    </main>
  );
}
